import {
  Component,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { HeaderBackBtnComponent } from '../../../../../../@commons/@components/app-header/header-back-btn/header-back-btn.component';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../../../../../../@commons/ui-components/shared.module';
import { FormsModule } from '@angular/forms';
import { LocationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/location-api';
import { TranslateModule } from '@ngx-translate/core';
import { CheckIconComponent } from '../../../../../../@icons/check-icon.component';
import { LocationType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/location-type.enum';
import { SunchineLevels } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/sunchine-levels.enum';
import { StorageService } from '../../../../../../@commons/@services/front/storage.service';
import { LocationResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/location.response';
import { ChoiceOption } from '../../../../../../@commons/ui-components/choice-selector/choice-selector.component';
import { readPremiumRefusal } from '../../../../../../@commons/@services/front/premium-error.util';
import { PremiumTeaserComponent } from '../../../../../../@commons/ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../../../../../../@commons/@toasts/premium-toast/premium-toast.component';

@Component({
  selector: 'vw-user-adress-location-form',
  imports: [
    PremiumTeaserComponent,
    PremiumToastComponent,
    HeaderBackBtnComponent,
    CommonModule,
    SharedModule,
    FormsModule,
    TranslateModule,
    CheckIconComponent,
  ],
  templateUrl: './user-adress-location-form.component.html',
  styleUrl: './user-adress-location-form.component.scss',
  providers: [
    {
      provide: LocationApi,
      useClass: LocationApi,
    },
  ],
})
export class UserAdressLocationFormComponent implements OnChanges, OnInit {
  /** Invitation premium affichee en contexte quand la limite est atteinte. */
  premiumMessageKey: string | null = null;
  premiumPaywallOpen = false;

  openPaywall(): void {
    this.premiumPaywallOpen = true;
  }

  @Input() location?: any;
  /** Affiche l'action « Supprimer cette zone » (uniquement en édition, ex. depuis Mon Potager). */
  @Input() showDelete = false;
  @Output() closeLocationForm = new EventEmitter<LocationResponse>();
  /** Émis quand l'utilisateur demande la suppression ; l'hôte gère la confirmation + l'appel API. */
  @Output() deleteLocation = new EventEmitter<any>();
  isSubmittingLocation: boolean = false;
  submitAttempted: boolean = false;

  // Types de zone = des LIEUX (une zone regroupe des planches). « Bac potager » est une planche, pas une zone → retiré.
  typeOptions: ChoiceOption[] = [
    {
      label: 'profile.addresses.edit-address.edit-locations.locations.GARDEN',
      value: 'GARDEN',
      icon: '🌱',
    },
    {
      label:
        'profile.addresses.edit-address.edit-locations.locations.GREENHOUSE',
      value: 'GREENHOUSE',
      icon: '🏡',
    },
    {
      label: 'profile.addresses.edit-address.edit-locations.locations.BALCONY',
      value: 'BALCONY',
      icon: '🪟',
    },
    {
      label: 'profile.addresses.edit-address.edit-locations.locations.TERRACE',
      value: 'TERRACE',
      icon: '☀️',
    },
    {
      label: 'profile.addresses.edit-address.edit-locations.locations.INDOOR',
      value: 'INDOOR',
      icon: '🛋️',
    },
  ];

  sunshineOptions: ChoiceOption[] = [
    {
      label:
        'profile.addresses.edit-address.edit-locations.sunshine.options.SHADED',
      value: 'SHADED',
    },
    {
      label:
        'profile.addresses.edit-address.edit-locations.sunshine.options.PARTIALY_SHADED',
      value: 'PARTIALY_SHADED',
    },
    {
      label:
        'profile.addresses.edit-address.edit-locations.sunshine.options.SUNNY',
      value: 'SUNNY',
    },
    {
      label:
        'profile.addresses.edit-address.edit-locations.sunshine.options.VERY_SUNNY',
      value: 'VERY_SUNNY',
    },
  ];

  name: string = '';
  selectedType: string = 'GARDEN';
  selectedSunshine: string = 'SHADED';
  width?: number;
  length?: number;

  constructor(
    private locationApi: LocationApi,
    private storageService: StorageService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    window.scrollTo(0, 0);
    this.setDefaultValues();
  }

  ngOnInit() {
    window.scrollTo(0, 0);
    this.setDefaultValues();
  }

  setDefaultValues() {
    this.submitAttempted = false;
    if (this.location) {
      this.name = this.location.name ?? '';
      // Repli sur le 1er type si l'ancien type (ex. PLANTER_BOX) n'est plus proposé.
      const hasType = this.typeOptions.some(
        (o) => o.value === this.location.locationType,
      );
      this.selectedType = hasType
        ? this.location.locationType
        : this.typeOptions[0].value;
      const hasSun = this.sunshineOptions.some(
        (o) => o.value === this.location.sunchineLevels,
      );
      this.selectedSunshine = hasSun
        ? this.location.sunchineLevels
        : this.sunshineOptions[0].value;
      this.width = this.location.width ?? undefined;
      this.length = this.location.length ?? undefined;
    } else {
      this.name = '';
      this.selectedType = this.typeOptions[0].value;
      this.selectedSunshine = this.sunshineOptions[0].value;
      this.width = undefined;
      this.length = undefined;
    }
  }

  get isNameValid(): boolean {
    return !!this.name && this.name.trim().length >= 2;
  }

  get derivedSurface(): number | null {
    return this.width && this.length
      ? Math.round(this.width * this.length)
      : null;
  }

  onCloseLocationForm() {
    this.closeLocationForm.emit();
  }

  onDeleteLocation() {
    this.deleteLocation.emit(this.location);
  }

  onNameChange(value: string) {
    this.name = value;
  }

  async onSubmitLocationForm() {
    this.submitAttempted = true;
    if (!this.isNameValid) {
      return;
    }
    this.isSubmittingLocation = true;
    let editedLocation: LocationResponse = null;
    try {
      if (this.location) {
        editedLocation = await this.locationApi.update({
          uid: this.location.uid,
          name: this.name.trim(),
          locationType: this.selectedType as LocationType,
          sunchineLevels: this.selectedSunshine as SunchineLevels,
          width: this.width || undefined,
          length: this.length || undefined,
        });
      } else {
        const addressUid = this.storageService.getItem('address-uid');
        // La surface n'est plus codée en dur : elle est dérivée des dimensions côté back.
        editedLocation = await this.locationApi.create({
          name: this.name.trim(),
          locationType: this.selectedType as LocationType,
          sunchineLevels: this.selectedSunshine as SunchineLevels,
          addressUid,
          width: this.width || undefined,
          length: this.length || undefined,
        } as any);
        this.location = undefined;
      }
    } catch (error) {
      const refusal = readPremiumRefusal(error);
      if (!refusal) throw error;
      this.premiumMessageKey = refusal.messageKey ?? 'premium.gate.zone';
      this.isSubmittingLocation = false;
      // On ne ferme pas le formulaire : l'utilisateur voit l'invitation en
      // contexte, sans perdre ce qu'il a saisi.
      return;
    } finally {
      this.isSubmittingLocation = false;
    }
    this.closeLocationForm.emit(editedLocation);
  }
}
